import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  LegacyAppointment,
  fetchAppointmentById
} from 'services/api/appointments';

// REST replacement for the shared GET_APPOINTMENT_ITEM Apollo cache: the
// detail page and its sub-tabs (Details, Files, Notes, Reports,
// AppointmentInfo, AddReportModal, AddOptionModal) all read one appointment
// and call refetchQueries after a mutation. Fetch once, share, refresh().
type ContextValue = {
  appointment: LegacyAppointment | null;
  loading: boolean;
  refresh: () => Promise<void>;
};

const AppointmentDetailsContext = createContext<ContextValue | undefined>(
  undefined
);

interface IProps {
  id?: string;
  children: ReactNode;
}

export const AppointmentDetailsProvider: FC<IProps> = ({ id, children }) => {
  const [appointment, setAppointment] = useState<LegacyAppointment | null>(
    null
  );

  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!id) {
      setAppointment(null);
      setLoading(false);

      return;
    }

    setLoading(true);

    try {
      setAppointment(await fetchAppointmentById(id));
    } catch {
      setAppointment(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(
    () => ({ appointment, loading, refresh: load }),
    [appointment, loading, load]
  );

  return (
    <AppointmentDetailsContext.Provider value={value}>
      {children}
    </AppointmentDetailsContext.Provider>
  );
};

export const useAppointmentDetails = (): ContextValue => {
  const context = useContext(AppointmentDetailsContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useAppointmentDetails must be used within an AppointmentDetailsProvider'
    );
  }

  return context;
};
